import { useCallback, useEffect, useRef, useState } from 'react'

import { useStepValidation } from './StepValidationContext'

type UseRegisterSaveGuardReturn = {
  open: boolean
  isSaving: boolean
  onSaveAndContinue: () => Promise<void>
  onDiscardAndContinue: () => void
  onCancel: () => void
}

/**
 * Registers a navigation guard that intercepts navigation while hasChanges
 * is true. Returns modal state that the component renders via SaveGuardModal.
 *
 * Usage:
 *   const saveGuard = useRegisterSaveGuard(hasChanges, handleSave)
 *   return (
 *     <>
 *       <SaveGuardModal {...saveGuard} />
 *       <YourContent />
 *     </>
 *   )
 */
export const useRegisterSaveGuard = (
  hasChanges: boolean,
  save: () => Promise<void>
): UseRegisterSaveGuardReturn => {
  const { setNavigationGuard } = useStepValidation()
  const [open, setOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const proceedRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    if (!hasChanges) {
      setNavigationGuard(null)
      return
    }

    setNavigationGuard((proceed) => {
      proceedRef.current = proceed
      setOpen(true)
    })

    return () => {
      setNavigationGuard(null)
    }
  }, [hasChanges, setNavigationGuard])

  const onSaveAndContinue = useCallback(async () => {
    setIsSaving(true)
    try {
      await save()
      setOpen(false)
      proceedRef.current?.()
      proceedRef.current = null
    } finally {
      setIsSaving(false)
    }
  }, [save])

  const onDiscardAndContinue = useCallback(() => {
    setOpen(false)
    proceedRef.current?.()
    proceedRef.current = null
  }, [])

  const onCancel = useCallback(() => {
    setOpen(false)
    proceedRef.current = null
  }, [])

  return {
    open,
    isSaving,
    onSaveAndContinue,
    onDiscardAndContinue,
    onCancel,
  }
}
